import { lazy, Suspense, useContext } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Device, DeviceDataContext } from './DeviceDataContext';
import { LoadingIndicator } from 'components/common/LoadingIndicator';

const MapContentComponent = lazy(() => import('./MapContentComponent'));

export type MapContainerProps = {
  selectedDevices: Device[];
  devicesToShow: Device[];
  onClickDevices: (devices: SelectOptions[]) => void;
  onRemoveDevices: (devices: SelectOptions[]) => void;
  viewOnly?: boolean;
};

/**
 * Abstraction layer for the map view. Decoupled from any specific data source
 * (SelectComponentModel, limitation state, etc.) — callers supply pre-computed
 * selectedDevices / devicesToShow and simple add/remove callbacks.
 *
 * mapsApiKey and language are read from the nearest DeviceDataContext.Provider.
 */
export const MapContainer: React.FC<MapContainerProps> = ({
  selectedDevices,
  devicesToShow,
  onClickDevices,
  onRemoveDevices,
  viewOnly = false
}) => {
  const { mapsApiKey, language } = useContext(DeviceDataContext);

  return (
    <APIProvider apiKey={mapsApiKey} language={language}>
      <Suspense fallback={<LoadingIndicator />}>
        <MapContentComponent
          onClickMapDevices={onClickDevices}
          onRemoveMapDevices={onRemoveDevices}
          selectedDevices={selectedDevices}
          devicesToShow={devicesToShow}
          viewOnly={viewOnly}
        />
      </Suspense>
    </APIProvider>
  );
};
